// NotificationPreferencesPanel — shown on its own screen (NotificationPreferencesPage). Per-category
// cards with In-app + Email + Push toggles, plus a global "Email me" master
// switch and a "Browser push" enable banner. Redesigned for friendlier UX:
// cards instead of a cramped grid, channel icons, contextual descriptions.
import { useEffect, useState } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';
import { useNotifications } from '../../context/NotificationContext';
import { isNative } from '../../lib/native';

const CATEGORY_LABEL = {
  booking:    { label: 'Bookings',          desc: 'Ticket confirmations and check-in reminders',     icon: 'ph-ticket',         color: 'bg-blue-50 text-blue-700' },
  payment:    { label: 'Payments',          desc: 'Receipts, refunds and payment failures',          icon: 'ph-credit-card',    color: 'bg-emerald-50 text-emerald-700' },
  tournament: { label: 'Tournaments',       desc: 'Fixtures, results and standings updates',         icon: 'ph-trophy',         color: 'bg-amber-50 text-amber-700' },
  live_match: { label: 'Live Match',        desc: 'Match start, wickets and milestones for your team', icon: 'ph-broadcast',    color: 'bg-rose-50 text-rose-700' },
  auction:    { label: 'Auction',           desc: 'Auction starts, your turn, won/lost a player',    icon: 'ph-gavel',          color: 'bg-purple-50 text-purple-700' },
  operations: { label: 'Operations',        desc: 'Umpire, scorer and streaming assignments',        icon: 'ph-clipboard-text', color: 'bg-cyan-50 text-cyan-700' },
  shop:       { label: 'Shop & Membership', desc: 'Order updates and membership renewals',           icon: 'ph-shopping-bag',   color: 'bg-pink-50 text-pink-700' },
};

const CHANNEL_META = {
  in_app: { label: 'In-app',  icon: 'ph-bell',           desc: 'Notification bell' },
  email:  { label: 'Email',   icon: 'ph-envelope-simple', desc: 'Inbox alert' },
  push:   { label: 'Push',    icon: 'ph-device-mobile',   desc: 'Device alert' },
};

export default function NotificationPreferencesPanel({ showHeading = true }) {
  const native = isNative();
  const { pushPermission, enablePush, disablePush } = useNotifications();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  const fetchPrefs = async (showToast = true) => {
    setLoading(true);
    setLoadError(false);
    // Retry once on 401/403 — Firebase token may not be rehydrated on first
    // mount after a hard reload.
    const tryGet = () => axiosInstance.get('/notifications/preferences');
    try {
      let r;
      try {
        r = await tryGet();
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          await new Promise((res) => setTimeout(res, 800));
          r = await tryGet();
        } else {
          throw err;
        }
      }
      setPrefs(r.data.categories || {});
    } catch (e) {
      setLoadError(true);
      if (showToast) toast.error('Could not load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (category, channel, value) => {
    if (saving) return;
    setSaving(true);
    setPrefs((prev) => ({ ...prev, [category]: { ...prev[category], [channel]: value } }));
    try {
      const res = await axiosInstance.put('/notifications/preferences', {
        preferences: [{ category, channel, enabled: value }],
      });
      setPrefs(res.data.categories);
    } catch (e) {
      toast.error('Failed to save preference');
      setPrefs((prev) => ({ ...prev, [category]: { ...prev[category], [channel]: !value } }));
    } finally {
      setSaving(false);
    }
  };

  const allEmailOn = prefs && Object.values(prefs).every((v) => v.email);
  const allPushOn  = prefs && Object.values(prefs).every((v) => v.push !== false);

  const toggleAllChannel = async (channel, value) => {
    if (saving) return;
    setSaving(true);
    const optimistic = {};
    Object.keys(prefs).forEach((k) => { optimistic[k] = { ...prefs[k], [channel]: value }; });
    setPrefs(optimistic);
    try {
      const payload = Object.keys(prefs).map((k) => ({ category: k, channel, enabled: value }));
      const res = await axiosInstance.put('/notifications/preferences', { preferences: payload });
      setPrefs(res.data.categories);
      toast.success(value ? `${CHANNEL_META[channel].label}s enabled` : `${CHANNEL_META[channel].label}s muted`);
    } catch (e) {
      toast.error('Failed to update master switch');
    } finally {
      setSaving(false);
    }
  };

  const handleEnablePush = async () => {
    setPushBusy(true);
    try {
      const r = await enablePush();
      if (r?.ok) {
        toast.success('Browser push enabled');
      } else if (r?.status === 'denied') {
        toast.error('You blocked notifications. Enable them in your browser settings.');
      } else if (r?.status === 'unsupported') {
        toast.error('Push not supported in this browser. Try Chrome, Firefox, or iOS PWA.');
      } else {
        toast.error('Could not enable push');
      }
    } finally {
      setPushBusy(false);
    }
  };

  const handleDisablePush = async () => {
    setPushBusy(true);
    try {
      await disablePush();
      toast.success('Browser push disabled');
    } finally {
      setPushBusy(false);
    }
  };

  if (loading || !prefs) {
    if (loadError) {
      return (
        <div
          className="bg-white rounded-2xl p-5 shadow-md border border-gray-100"
          data-testid="notification-prefs-error"
        >
          <h3 className="font-extrabold text-[#2B3A67]">Notification Preferences</h3>
          <p className="text-xs text-gray-500 mt-1">
            We couldn&apos;t load your preferences. Please try again.
          </p>
          <button
            onClick={() => fetchPrefs(false)}
            data-testid="notification-prefs-retry"
            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2B3A67] text-white text-xs font-bold hover:bg-[#1f2a4d] transition"
          >
            <i className="ph-bold ph-arrow-clockwise" /> Retry
          </button>
        </div>
      );
    }
    return (
      <div className="bg-white rounded-2xl p-5 shadow-md border border-gray-100" data-testid="notification-prefs-loading">
        <div className="flex items-center gap-3 text-sm text-gray-400 py-4">
          <span className="w-4 h-4 border-2 border-gray-300 border-t-[#E54562] rounded-full animate-spin" />
          Loading notification preferences…
        </div>
      </div>
    );
  }

  return (
    <section
      data-testid="notification-prefs-panel"
      className="bg-white rounded-2xl p-5 shadow-md border border-gray-100 space-y-4"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          {showHeading && <h3 className="font-extrabold text-[#2B3A67]">Notification Preferences</h3>}
          <p className="text-[11px] text-gray-500 mt-0.5">
            Choose how Backyard reaches you. Tap a channel to mute or unmute it for each topic.
          </p>
        </div>
      </div>

      {/* Browser Push card */}
      <div
        data-testid="prefs-push-banner"
        className="flex items-center justify-between bg-gradient-to-br from-[#2B3A67] to-[#3AAFA9] rounded-2xl p-4 text-white"
      >
        <div className="flex-1 min-w-0 mr-3">
          <p className="font-bold text-sm flex items-center gap-1.5">
            <i className="ph-bold ph-bell-ringing" /> {native ? 'Phone notifications' : 'Browser push'}
          </p>
          <p className="text-[11px] text-white/80 mt-0.5">
            {native ? (
              <>
                {pushPermission === 'granted' && 'On for this phone. Choose topics below.'}
                {pushPermission === 'default' && 'Get real-time alerts even when Backyard isn\'t open.'}
                {pushPermission === 'denied' && 'Turned off for Backyard. Turn them on in your phone\'s Settings.'}
              </>
            ) : (
              <>
                {pushPermission === 'granted' && 'Active on this device — you\'ll get pushes even when the tab is closed.'}
                {pushPermission === 'default' && 'Get real-time alerts even when Backyard isn\'t open.'}
                {pushPermission === 'denied' && 'Blocked by your browser. Update settings to re-enable.'}
                {pushPermission === 'unsupported' && 'Not supported here. Try Chrome, Firefox, or iOS Safari (16.4+ as PWA).'}
              </>
            )}
          </p>
        </div>
        {/* In the apps the phone's Settings own the on/off; topics are below. */}
        {pushPermission === 'granted' && !native ? (
          <button
            data-testid="prefs-push-disable"
            onClick={handleDisablePush}
            disabled={pushBusy}
            className="px-3 py-1.5 rounded-full bg-white/15 text-white text-xs font-bold hover:bg-white/25 disabled:opacity-60 backdrop-blur"
          >
            Disable
          </button>
        ) : pushPermission === 'default' ? (
          <button
            data-testid="prefs-push-enable"
            onClick={handleEnablePush}
            disabled={pushBusy}
            className="px-3 py-1.5 rounded-full bg-white text-[#2B3A67] text-xs font-bold hover:bg-white/90 disabled:opacity-60 whitespace-nowrap"
          >
            {pushBusy ? '…' : 'Enable'}
          </button>
        ) : null}
      </div>

      {/* Master switches */}
      <div className="grid grid-cols-2 gap-3" data-testid="prefs-master-switches">
        <MasterSwitch
          icon="ph-envelope-simple"
          label="All emails"
          enabled={allEmailOn}
          onToggle={() => toggleAllChannel('email', !allEmailOn)}
          testId="prefs-master-email"
        />
        <MasterSwitch
          icon="ph-device-mobile"
          label="All push"
          enabled={allPushOn}
          onToggle={() => toggleAllChannel('push', !allPushOn)}
          testId="prefs-master-push"
        />
      </div>

      {/* Per-category cards */}
      <div className="space-y-2.5" data-testid="prefs-category-list">
        {Object.entries(CATEGORY_LABEL).map(([key, meta]) => {
          const row = prefs[key] || { in_app: true, email: true, push: true };
          return (
            <div
              key={key}
              data-testid={`prefs-row-${key}`}
              className="bg-gray-50 rounded-2xl p-3.5 border border-gray-100"
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${meta.color}`}>
                  <i className={`ph-bold ${meta.icon} text-lg`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[#2B3A67]">{meta.label}</p>
                  <p className="text-[11px] text-gray-500 leading-snug">{meta.desc}</p>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2">
                <ChannelToggle
                  channel="in_app"
                  enabled={row.in_app}
                  onToggle={(v) => toggle(key, 'in_app', v)}
                  testId={`prefs-${key}-in_app`}
                />
                <ChannelToggle
                  channel="email"
                  enabled={row.email}
                  onToggle={(v) => toggle(key, 'email', v)}
                  testId={`prefs-${key}-email`}
                />
                <ChannelToggle
                  channel="push"
                  enabled={row.push !== false}
                  onToggle={(v) => toggle(key, 'push', v)}
                  testId={`prefs-${key}-push`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function MasterSwitch({ icon, label, enabled, onToggle, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onToggle}
      aria-pressed={enabled}
      className={`flex items-center justify-between gap-2 px-3.5 py-3 rounded-2xl border transition-all ${
        enabled
          ? 'bg-[#2B3A67] border-[#2B3A67] text-white shadow-sm'
          : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
      }`}
    >
      <div className="flex items-center gap-2 text-xs font-bold">
        <i className={`ph-bold ${icon}`} />
        {label}
      </div>
      <span
        className={`w-9 h-5 rounded-full relative transition shrink-0 ${
          enabled ? 'bg-emerald-400' : 'bg-gray-300'
        }`}
      >
        <span
          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition ${
            enabled ? 'left-[18px]' : 'left-0.5'
          }`}
        />
      </span>
    </button>
  );
}

function ChannelToggle({ channel, enabled, onToggle, testId }) {
  const meta = CHANNEL_META[channel];
  return (
    <button
      data-testid={testId}
      onClick={() => onToggle(!enabled)}
      aria-pressed={enabled}
      className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border-2 transition-all ${
        enabled
          ? 'bg-white border-[#2B3A67] text-[#2B3A67] shadow-sm'
          : 'bg-white/60 border-gray-200 text-gray-400 hover:border-gray-300'
      }`}
    >
      <div className="flex items-center gap-1.5">
        <i className={`ph-bold ${meta.icon} text-base`} />
        <span className="text-[11px] font-bold">{meta.label}</span>
      </div>
      <span className={`text-[9px] font-bold uppercase tracking-wider ${
        enabled ? 'text-emerald-600' : 'text-gray-400'
      }`}>
        {enabled ? 'On' : 'Off'}
      </span>
    </button>
  );
}
